import { useState } from "react";
import RoleBadge from "../../../components/Profile/RoleBadge/RoleBadge";
import requestHandler from "../../../utils/requestHandler";
import AdminBlock from "./AdminBlock";
import strings from "../../../locales/ro.json";
import "./RolesManager.css";

const UserRoleAssigner = ({ users, roles, onAssign, onRemove, loading, authToken }) => {
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [userRoles, setUserRoles] = useState([]);
  const [loadingUser, setLoadingUser] = useState(false);

  const filteredUsers = search.trim()
    ? users.filter((u) =>
        u.name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
          .includes(search.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, ""))
      ).slice(0, 8)
    : [];

  const selectUser = async (user) => {
    setSelectedUser(user);
    setSearch(user.name);
    setLoadingUser(true);
    try {
      const uid = user.email.split("@")[0];
      const fullUser = await requestHandler(`users/${uid}`, "GET", authToken);
      setUserRoles(fullUser?.roles || []);
    } catch (err) {
      console.error("Error fetching user roles:", err);
      setUserRoles([]);
    } finally {
      setLoadingUser(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedUser || !selectedRoleId) return;
    try {
      await onAssign(selectedUser.email, Number(selectedRoleId));
      // Refresh user roles
      await selectUser(selectedUser);
      setSelectedRoleId("");
    } catch (_) {
      // error handled in hook
    }
  };

  const handleRemove = async (roleId) => {
    if (!selectedUser) return;
    try {
      await onRemove(selectedUser.email, roleId);
      setUserRoles((prev) => prev.filter((r) => r.id !== roleId));
    } catch (_) {
      // error handled in hook
    }
  };

  const clearSelection = () => {
    setSelectedUser(null);
    setSearch("");
    setUserRoles([]);
    setSelectedRoleId("");
  };

  // Roles not yet assigned to this user
  const availableRoles = roles.filter(
    (r) => !userRoles.some((ur) => ur.id === r.id)
  );

  return (
    <AdminBlock title={strings.admin.roles.assigner.title} defaultOpen={true}>
      <div className="rm-search-container">
        <input
          type="text"
          className="rm-search-input"
          placeholder={strings.admin.roles.assigner.searchPlaceholder}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            if (selectedUser && e.target.value !== selectedUser.name) {
              setSelectedUser(null);
              setUserRoles([]);
            }
          }}
        />
        {search && selectedUser && (
          <button className="rm-clear-btn" onClick={clearSelection} title={strings.admin.roles.assigner.resetTitle}>✕</button>
        )}
      </div>

      {/* Dropdown results */}
      {!selectedUser && filteredUsers.length > 0 && (
        <div className="rm-search-results">
          {filteredUsers.map((u) => (
            <div key={u.id} className="rm-search-result" onClick={() => selectUser(u)}>
              <span className="rm-result-name">{u.name}</span>
              <span className="rm-result-email">{u.email}</span>
            </div>
          ))}
        </div>
      )}

      {/* Selected user panel */}
      {selectedUser && (
        <div className="rm-user-panel">
          <div className="rm-user-panel-header">
            <strong>{selectedUser.name}</strong>
            <span className="rm-user-panel-email">{selectedUser.email}</span>
          </div>

          {/* Current roles */}
          <div className="rm-user-current-roles">
            <label>{strings.admin.roles.assigner.currentRolesLabel}</label>
            {loadingUser ? (
              <span className="rm-loading-text">{strings.admin.roles.assigner.loading}</span>
            ) : userRoles.length > 0 ? (
              <div className="rm-current-badges">
                {userRoles.map((role) => (
                  <div key={role.id} className="rm-removable-badge">
                    <RoleBadge name={role.name} color={role.color} />
                    <button
                      className="rm-badge-remove"
                      onClick={() => handleRemove(role.id)}
                      disabled={loading}
                      title={strings.admin.roles.assigner.removeTitle}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <span className="rm-no-roles">{strings.admin.roles.assigner.noRoles}</span>
            )}
          </div>

          {/* Assign new role */}
          {availableRoles.length > 0 && (
            <div className="rm-assign-row">
              <select
                value={selectedRoleId}
                onChange={(e) => setSelectedRoleId(e.target.value)}
                className="rm-assign-select"
              >
                <option value="">{strings.admin.roles.assigner.addRolePlaceholder}</option>
                {availableRoles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} ({r.role_type?.label})
                  </option>
                ))}
              </select>
              <button
                className="rm-assign-btn"
                onClick={handleAssign}
                disabled={!selectedRoleId || loading}
              >
                {strings.admin.roles.assigner.assignBtn}
              </button>
            </div>
          )}
        </div>
      )}
    </AdminBlock>
  );
};

export default UserRoleAssigner;
